import React from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/cn";

const iconButtonStyles = cva(
  "inline-flex items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary: "bg-primary-900 text-white hover:bg-primary-800",
        accent: "bg-accent-500 text-white hover:bg-accent-600",
        secondary:
          "bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800",
        ghost:
          "bg-transparent text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800",
        danger: "bg-danger-500 text-white hover:bg-danger-700",
        "outline-primary":
          "bg-white dark:bg-neutral-900 border border-primary-900 dark:border-primary-400 text-primary-900 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30",
        warning:
          "bg-transparent text-warning-600 dark:text-warning-500 hover:bg-warning-50 dark:hover:bg-warning-500/10",
      },
      size: {
        sm: "h-8 w-8",
        md: "h-10 w-10",
        lg: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "ghost",
      size: "md",
    },
  }
);

/**
 * IconButton - square, icon-only button. Requires aria-label.
 * @param {"primary"|"accent"|"secondary"|"ghost"|"danger"|"outline-primary"|"warning"} [variant="ghost"]
 * @param {"sm"|"md"|"lg"} [size="md"]
 * @param {boolean} [disabled]
 * @param {string} ariaLabel - required, describes the action
 * @param {React.ReactNode} children - the icon
 * @param {string} [className]
 */
export const IconButton = React.forwardRef(function IconButton(
  {
    variant = "ghost",
    size = "md",
    disabled = false,
    "aria-label": ariaLabel,
    className,
    children,
    ...props
  },
  ref
) {
  if (!ariaLabel && process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(
      "IconButton: an `aria-label` prop is required for accessibility."
    );
  }

  return (
    <button
      ref={ref}
      type={props.type || "button"}
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      className={cn(iconButtonStyles({ variant, size }), className)}
      {...props}
    >
      {children}
    </button>
  );
});
